import { TranslateService } from "@ngx-translate/core";

import { EnvironmentInjector, Injectable, effect, inject, runInInjectionContext, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, RouterStateSnapshot, TitleStrategy } from "@angular/router";

import { TitleFn } from "./translate-title-resolver";

@Injectable({
  providedIn: "root",
})
export class ReuseTitleResolverStrategy extends TitleStrategy {
  /**
   * Title strategy that (1) responds to language changes and (2) allows
   * reusing title resolvers.
   *
   * Logic wise it is based on Angular's default title resolver, but it is
   * augmented by also extracting the title resolver from route configs.
   * To do so it also needs to set the title asynchronously.
   *
   * Centralizing the update logic in this strategy also means we don't
   * need to manually add title update logic to each page component when
   * we already configure the title in the route config.
   */
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(EnvironmentInjector);

  private readonly onLangChange = toSignal(this.translate.onLangChange);
  private readonly routeSnapshot = signal<RouterStateSnapshot | undefined>(undefined);

  constructor() {
    super();

    effect(() => {
      this.onLangChange(); // Statement is necessary to trigger effect on lang change
      const snapshot = this.routeSnapshot();
      if (snapshot) {
        const [activeRoute, titleUpdate] = this.findActiveRouteAndTitle(snapshot);
        this._updateTitle(activeRoute, snapshot, titleUpdate);
      }
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    // Setting the snapshot triggers the effect that actually updates the title
    this.routeSnapshot.set(snapshot);
  }

  private _updateTitle(
    activeRoute: ActivatedRouteSnapshot,
    snapshot: RouterStateSnapshot,
    update: TitleFn | string | undefined,
  ): void {
    if (isFunction(update)) {
      const doUpdate = (): Promise<void> => update(activeRoute, snapshot).then((s) => this.title.setTitle(s));
      runInInjectionContext(this.injector, doUpdate);
    } else {
      if (update !== undefined) this.title.setTitle(update);
    }
  }

  findActiveRouteAndTitle(snapshot: RouterStateSnapshot): [ActivatedRouteSnapshot, TitleFn | string | undefined] {
    /**
     * Based on the route snapshot, find the active route and deepest title.
     *
     * This is used to extract the title resolvers from route configs, so we
     * can reuse them when the language changes. The activate route is also extracted
     * to pass on the parameter map which some title resolvers need.
     *
     * The title can be a resolver but can actually also be hardcoded string or it
     * can be absent. It also is not necessarily set on the deepest route config,
     * but may be set at a higher level.
     */
    let pageTitle: TitleFn | string | undefined;
    let route: ActivatedRouteSnapshot | undefined = snapshot.root;
    let activeRoute: ActivatedRouteSnapshot = route;
    while (route !== undefined) {
      // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
      pageTitle = route?.routeConfig?.title ?? this.getResolvedTitleForRoute(route) ?? pageTitle;
      activeRoute = route;
      route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
    }

    return [activeRoute, pageTitle];
  }
}

function isFunction(value: unknown): value is TitleFn {
  return typeof value === "function";
}
